import { useState } from 'react';
import useFindSkusQuery from '@/api/queries/useFindSkusQuery';
import useGetProductListSkuIdsQuery from '@/api/queries/useGetProductListSkuIdsQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import StatusActiveIndicator from '@/components/StatusActiveIndicator';
import { Sku, SkuAssociation } from '@/models/Sku';
import { isStatusActive } from '@/models/Status';
import ProductListEmptyState from '@/modules/product/components/ProductListEmptyState';
import ProductListErrorState from '@/modules/product/components/ProductListErrorState';
import { TrashIcon } from '@heroicons/react/24/outline';

interface ProductListProductTableProps {
  productListId: string;
  stagedItems: Sku[];
  onUnstage: (skuId: string) => void;
  page?: number;
  onChangePage?: (page: number) => void;
  onRemove: (skuId: string) => Promise<void>;
}

const ProductListProductTable = ({
  productListId,
  stagedItems,
  onUnstage,
  page = 1,
  onChangePage = () => {},
  onRemove,
}: ProductListProductTableProps) => {
  const { skuIds, itemsPerPage, isLoading, error, invalidateQuery } = useGetProductListSkuIdsQuery(
    productListId,
    page,
  );
  const ids = skuIds.items;
  const {
    skus,
    isLoading: skusLoading,
    invalidateQuery: invalidateSkus,
  } = useFindSkusQuery(
    1,
    undefined,
    { skuId: ids },
    undefined,
    [SkuAssociation.PRODUCT],
    ids.length > 0,
  );
  const [removingId, setRemovingId] = useState<string | null>(null);

  if (error) {
    return (
      <ProductListErrorState
        message="Unable to load the products in this list."
        onRetry={() => void invalidateQuery()}
      />
    );
  }

  const loading = isLoading || (ids.length > 0 && skusLoading);
  const persistedItems = skus.items;

  if (!loading && stagedItems.length === 0 && persistedItems.length === 0) {
    return (
      <ProductListEmptyState
        title="No products in this list yet"
        description="Select a product above to add an SKU to this Product List."
      />
    );
  }

  const handleRemove = async (sku: Sku) => {
    setRemovingId(sku.id);
    try {
      await onRemove(sku.id);
      await invalidateSkus();

      if (ids.length === 1 && page > 1) {
        onChangePage(page - 1);
      }
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={loading ? 'loading' : ''}>
          <thead>
            <tr>
              <th>SKU Code</th>
              <th>Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stagedItems.map((sku) => (
              <tr key={`staged-${sku.id}`} className="bg-blue-50">
                <td>{sku.externalCode}</td>
                <td>
                  <div>{sku.name}</div>
                </td>
                <td>
                  <StatusActiveIndicator isActive={isStatusActive(sku)} />
                </td>
                <td>
                  <Button
                    variant="delete"
                    size="xs"
                    onClick={() => onUnstage(sku.id)}
                    icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {persistedItems.map((sku) => (
              <tr key={sku.id}>
                <td>{sku.externalCode}</td>
                <td>
                  <div>{sku.name}</div>
                </td>
                <td>
                  <StatusActiveIndicator isActive={isStatusActive(sku)} />
                </td>
                <td>
                  <Confirm
                    onConfirm={() => handleRemove(sku)}
                    title="Remove product"
                    message={`Are you sure you want to remove "${sku.name}" from this Product List? This can change store catalogue and order availability immediately.`}
                    confirmText="Remove"
                    danger
                  >
                    {(handleClick, loading) => (
                      <Button
                        onClick={handleClick}
                        loading={loading || removingId === sku.id}
                        variant="delete"
                        size="xs"
                        icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                      >
                        Remove
                      </Button>
                    )}
                  </Confirm>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={skuIds.totalCount || 0}
          estimatedTotalCount={skuIds.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductListProductTable;
